import React, { useEffect, useRef, useMemo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import * as d3 from "d3";

const PublicationTimeline = ({ items, category, onItemClick, onReset }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 90 });
  const [zoomDomain, setZoomDomain] = useState(null);

  // Process timeline data similar to EdgeGraph's data processing
  const timelineData = useMemo(() => {
    let debugCount = 0;
    const dates = items
      .map(item => {
        let date = null;
        const entity = item.entity || item;

        if (category === "patent" && entity.publication_date) {
          date = new Date(entity.publication_date);
          // Debug: log first few patents to check date parsing
          if (debugCount < 5) {
            console.log('Patent date:', entity.publication_date, '→', date.toISOString(), entity);
            debugCount++;
          }
        } else if (category === "publication") {
          if (entity.publication_date) {
            date = new Date(entity.publication_date);
          } else if (entity.citation) {
            const yearMatch = entity.citation.match(/\b(19|20)\d{2}\b/);
            if (yearMatch) {
              date = new Date(parseInt(yearMatch[0]), 0, 1);
            }
          }
        }

        return date && !isNaN(date.getTime())
          ? { date, item, entity }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.date - b.date);

    if (dates.length === 0) {
      return null;
    }

    const minDate = dates[0].date;
    const maxDate = dates[dates.length - 1].date;

    return { dates, minDate, maxDate };
  }, [items, category]);

  // Create histogram bins
  const histogramData = useMemo(() => {
    if (!timelineData || !dimensions.width) return null;

    const { dates, minDate, maxDate } = timelineData;
    const currentDomain = zoomDomain || [minDate, maxDate];
    const scale = d3.scaleTime()
      .domain(currentDomain)
      .range([20, dimensions.width - 20]);

    // Determine time-based binning interval based on the current (possibly zoomed) time range
    const timeRange = currentDomain[1] - currentDomain[0];
    const yearsSpan = timeRange / (365.25 * 24 * 60 * 60 * 1000);

    let binInterval;
    if (yearsSpan > 10) {
      // For ranges > 10 years, bin by year
      binInterval = d3.timeYear;
    } else if (yearsSpan > 2) {
      // For ranges 2-10 years, bin by quarter
      binInterval = d3.timeMonth.every(3);
    } else if (yearsSpan > 0.2) {
      // For ranges > 2.5 months (ensures quarters zoom to months), bin by month
      binInterval = d3.timeMonth;
    } else {
      // For ranges < 2.5 months, bin by day
      binInterval = d3.timeDay;
    }

    // Create histogram bins
    const histogram = d3.histogram()
      .value(d => d.date)
      .domain(scale.domain())
      .thresholds(binInterval.range(...scale.domain()));

    const bins = histogram(dates);

    // Calculate max count for scaling bar heights
    const maxCount = d3.max(bins, d => d.length) || 1;

    return { bins, scale, minDate, maxDate, maxCount, binInterval };
  }, [timelineData, dimensions.width, zoomDomain]);

  // Handle resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        setDimensions({ width, height: 90 });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Handle bar click
  const handleBarClick = useCallback((bin) => {
    if (bin.length === 1) {
      // For single items, filter to that item
      if (onItemClick) {
        onItemClick(bin[0].item);
      }
    }

    // Zoom into the bin's date range
    setZoomDomain([bin.x0, bin.x1]);
  }, [onItemClick]);

  // Reset zoom
  const handleReset = useCallback(() => {
    setZoomDomain(null);
    if (onReset) {
      onReset();
    }
  }, [onReset]);

  // Render D3 visualization
  useEffect(() => {
    if (!svgRef.current || !histogramData || !dimensions.width) return;

    const svg = d3.select(svgRef.current);
    const { bins, scale, maxCount, binInterval } = histogramData;

    // Clear previous content
    svg.selectAll("*").remove();

    // Create main group
    const g = svg.append("g");

    // Reserve fixed space for axis labels at bottom
    const LABEL_SPACE = 18; // Fixed space for tick labels
    const BAR_AREA_HEIGHT = dimensions.height - LABEL_SPACE;

    // Height scale for bars
    const BAR_HEIGHT_MAX = BAR_AREA_HEIGHT * 0.75;
    const MIN_BAR_HEIGHT = 2; // Minimum height for bars with count > 0
    const heightScale = d3.scaleLinear()
      .domain([0, maxCount])
      .range([MIN_BAR_HEIGHT, BAR_HEIGHT_MAX]);

    // Generate tick values that align with the bin interval, but limit to reasonable count
    let tickValues = binInterval.range(...scale.domain());
    // If there are too many ticks (>12), thin them out by taking every Nth
    if (tickValues.length > 12) {
      const step = Math.ceil(tickValues.length / 10);
      tickValues = tickValues.filter((_, i) => i % step === 0);
    }
    const allJanuary = tickValues.every(d => d.getMonth() === 0);
    const allSameMonth = tickValues.length > 1 &&
      tickValues.every(d => d.getMonth() === tickValues[0].getMonth() &&
                           d.getFullYear() === tickValues[0].getFullYear());

    // Add axis with smart tick formatting - positioned with space for labels below
    const xAxis = d3.axisBottom(scale)
      .tickValues(tickValues)
      .tickSize(4)
      .tickFormat(date => {
        // If all ticks are in same month, just show day number
        if (allSameMonth) {
          return d3.timeFormat("%d")(date);
        }
        // If all ticks are in January, just show year
        if (allJanuary) {
          return d3.timeFormat("%Y")(date);
        }
        // Otherwise show month + year
        return d3.timeFormat("%b %Y")(date);
      });

    const axisGroup = g.append("g")
      .attr("class", "timeline-axis")
      .attr("transform", `translate(0, ${BAR_AREA_HEIGHT})`)
      .call(xAxis);

    // Style axis for better readability
    axisGroup.selectAll("text")
      .style("font-size", "11px")
      .style("fill", "#666");

    // Style tick lines and domain line to be visible
    axisGroup.selectAll("line")
      .style("stroke", "#999")
      .style("stroke-width", 1);

    axisGroup.select(".domain")
      .style("stroke", "#ccc")
      .style("stroke-width", 1);

    // Add brush for drag-select zoom
    const brush = d3.brushX()
      .extent([[20, 0], [dimensions.width - 20, BAR_AREA_HEIGHT - 4]])
      .on("end", (event) => {
        if (!event.selection) return;

        const [x0, x1] = event.selection;
        const newDomain = [scale.invert(x0), scale.invert(x1)];

        // Clear brush
        g.select(".brush").call(brush.move, null);

        // Apply zoom
        setZoomDomain(newDomain);
      });

    g.append("g")
      .attr("class", "brush")
      .call(brush);

    // Add histogram bars
    const bars = g.selectAll(".timeline-bar")
      .data(bins)
      .enter()
      .append("g")
      .attr("class", "timeline-bar-group")
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        event.stopPropagation();
        handleBarClick(d);
      });

    // Fixed gap between bars
    const BAR_GAP = 1;
    const baselineY = BAR_AREA_HEIGHT - 4; // Baseline 4px above axis

    // Add rectangles - time-proportional width minus fixed gap
    bars.append("rect")
      .attr("class", "timeline-bar")
      .attr("x", d => scale(d.x0))
      .attr("y", d => baselineY - (d.length > 0 ? heightScale(d.length) : 0))
      .attr("width", d => Math.max(0, scale(d.x1) - scale(d.x0) - BAR_GAP))
      .attr("height", d => d.length > 0 ? heightScale(d.length) : 0)
      .attr("fill", "#3126B4")
      .attr("stroke", "none");

    // Create hover label group with background
    const hoverLabelGroup = g.append("g")
      .attr("class", "timeline-hover-label-group")
      .style("pointer-events", "none")
      .style("opacity", 0);

    const hoverLabelBg = hoverLabelGroup.append("rect")
      .attr("class", "timeline-hover-label-bg")
      .attr("fill", "#ffffff")
      .attr("stroke", "#ccc")
      .attr("stroke-width", 1)
      .attr("rx", 3);

    const hoverLabel = hoverLabelGroup.append("text")
      .attr("class", "timeline-hover-label")
      .attr("text-anchor", "middle")
      .style("font-size", "12px")
      .style("font-weight", "500")
      .style("fill", "#333");

    // Add hover effects with immediate label display
    bars.on("mouseenter", function(event, d) {
      const count = d.length;

      // Format date range intelligently
      let dateRange;
      const startYear = d.x0.getFullYear();
      const endYear = d.x1.getFullYear();
      const startMonth = d.x0.getMonth();
      const endMonth = d.x1.getMonth();

      // If bin spans a year boundary and both dates are in January, just show the year
      if (startMonth === 0 && endMonth === 0 && endYear === startYear + 1) {
        dateRange = `${startYear}`;
      } else {
        const startDate = d3.timeFormat("%b %Y")(d.x0);
        const endDate = d3.timeFormat("%b %Y")(d.x1);
        dateRange = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
      }

      // Update hover label - center on bar
      const labelText = `${count} (${dateRange})`;
      const barWidth = scale(d.x1) - scale(d.x0) - BAR_GAP;
      const barX = scale(d.x0) + barWidth / 2;

      hoverLabel
        .text(labelText)
        .attr("x", barX)
        .attr("y", 10);

      // Get text dimensions and position background
      const bbox = hoverLabel.node().getBBox();
      const padding = 4;
      hoverLabelBg
        .attr("x", bbox.x - padding)
        .attr("y", bbox.y - padding)
        .attr("width", bbox.width + padding * 2)
        .attr("height", bbox.height + padding * 2);

      hoverLabelGroup.style("opacity", 1);

      // Highlight bar
      d3.select(this).select("rect")
        .attr("fill", "#2519a0");
    }).on("mouseleave", function() {
      // Hide label
      hoverLabelGroup.style("opacity", 0);

      // Reset bar color
      d3.select(this).select("rect")
        .attr("fill", "#3126B4");
    });

  }, [histogramData, dimensions, handleBarClick]);

  if (!timelineData) {
    return null;
  }

  return (
    <div className="publication-timeline-container" ref={containerRef}>
      {zoomDomain && (
        <button
          className="timeline-reset-button glyphicon glyphicon-refresh"
          onClick={handleReset}
          title="Reset zoom"
          aria-label="Reset zoom"
        />
      )}
      <div className="publication-timeline">
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          style={{ display: 'block', overflow: 'visible' }}
        />
      </div>
    </div>
  );
};

PublicationTimeline.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  category: PropTypes.string.isRequired,
  onItemClick: PropTypes.func,
  onReset: PropTypes.func,
};

export default PublicationTimeline;